import React, { FC, useCallback, useEffect, useState } from 'react';
import {
  ForgeDialog,
  ForgeIcon,
  ForgeIconButton,
  ForgeScaffold,
  ForgeTextField,
  ForgeToolbar
} from '@tylertech/forge-react';

import './index.scss';
import { showSuccessToastNow } from 'common/components/ToastNotification/Toastmaster';
import { useEscapeKey } from 'common/visualizations/views/agGridReact/customHooks/useEscapeKey';
import { I18n } from 'common/visualizations';
import { View } from 'common/types/view';
import RecipientsField from './components/RecipientsField/RecipientsField';
import AttachmentSection from './components/Attachment/AttachmentSection';
import { tablesFromStoryData } from './components/Attachment/helpers';
import { DEFAULT_ATTACHMENT_CONFIG } from './constants';
import { ScheduleSendValues } from './types/scheduleSend';
import FrequencySelector from '../FrequencySelector';
import { FrequencyConfig } from 'common/types/frequencySelector/frequencySelector';
import { frequencyConfigDefaulValues } from './constants/defaultVal';
import SummarySection from './components/Summary/SummarySection';
import List from './components/Preview/Attachment/List';
import PreviewFooter from './components/Preview/Footer';
import Footer from './components/Footer';

const scope = 'shared.components.schedule_send_report.modal';

export interface ScheduleSendProps {
  view: View;
  onDismiss: () => void;
  scheduledSendModalVisible: boolean;
  setScheduledSendModalVisible: React.Dispatch<React.SetStateAction<boolean>>;
}

type Action = 'create' | 'preview' | 'edit' | 'update' | 'delete';

const ScheduleSendReportModal: FC<ScheduleSendProps> = (props) => {
  const { onDismiss, scheduledSendModalVisible, setScheduledSendModalVisible, view } = props;
  const defaultValues: ScheduleSendValues = {
    name: view.name,
    subject: view.name,
    recipients: [],
    attachmentConfigs: [
      {
        value: DEFAULT_ATTACHMENT_CONFIG.value,
        format: DEFAULT_ATTACHMENT_CONFIG.format,
        icon: DEFAULT_ATTACHMENT_CONFIG.icon
      }
    ],
    frequencyConfig: frequencyConfigDefaulValues
  };

  const [scheduleSendValues, setScheduleSendValues] = useState<ScheduleSendValues>(defaultValues);
  const [frequencyConfig, setFrequencyConfig] = useState<FrequencyConfig>(frequencyConfigDefaulValues);
  const [prevAction, setPrevAction] = useState<Action>('create');
  const [isOnReadOnlyMode, setIsOnReadOnlyMode] = useState(false);

  /**
   * Updates a specific key in the ScheduleSendValues state.
   *
   * @template K - A key of ScheduleSendValues: 'name', 'subject', 'recipients', 'attachmentConfigs', or 'frequencyConfig'.
   * @param key - The specific field to update in ScheduleSendValues.
   *   - `'name'`: string
   *   - `'subject'`: string
   *   - `'recipients'`: string[]
   *   - `'attachmentConfigs'`: AttachmentConfig[]
   *   - `'frequencyConfig'`: FrequencyConfig
   * @param value - The new value for the specified key, matching its expected type.
   *
   * @example
   * updateScheduleSendValue('name', 'My Schedule');
   * updateScheduleSendValue('recipients', ['user@example.com']);
   */
  const updateScheduleSendValue = useCallback(
    <K extends keyof ScheduleSendValues>(key: K, value: ScheduleSendValues[K]) => {
      setScheduleSendValues((prev) => ({
        ...prev,
        [key]: value
      }));
    },
    []
  );

  useEffect(() => {
    // update this when API is finished
    setScheduleSendValues(defaultValues);
  }, [view]);

  useEffect(() => {
    updateScheduleSendValue('frequencyConfig', frequencyConfig);
  }, [frequencyConfig]);

  useEffect(() => {
    setIsOnReadOnlyMode(prevAction === 'preview');
  }, [prevAction]);

  // If a user clicks the esc button, we are closing the modal
  useEscapeKey(onDismiss, scheduledSendModalVisible);

  async function onSaveConfiguration() {
    showSuccessToastNow(I18n.t('success_toast', { scope }));
    setScheduledSendModalVisible(false);
  }

  const onPreview = async () => {
    setPrevAction('preview');
  };

  const onEdit = async () => {
    setPrevAction('edit');
  };

  const onDelete = async () => {
    setPrevAction('delete');
    onDismiss();
    showSuccessToastNow(I18n.t('delete_toast', { scope }));
  };

  const onUpdate = async () => {
    await setPrevAction('update');
    onDismiss();
    showSuccessToastNow(I18n.t('update_toast', { scope }));
  };

  const textFieldAttrs = {
    labelPosition: isOnReadOnlyMode ? 'inset' : 'block-start',
    required: !isOnReadOnlyMode,
    variant: isOnReadOnlyMode ? 'plain' : 'outlined',
    disabled: isOnReadOnlyMode,
    className: isOnReadOnlyMode ? 'read-only' : ''
  };

  return (
    <ForgeDialog
      open={scheduledSendModalVisible}
      on-forge-dialog-close={onDismiss}
      persistent
      data-testid="schedule-send-dialog"
      aria-labelledby="schedule-send-modal-title"
    >
      <ForgeScaffold className="schedule-send-modal">
        <div slot="header">
          <ForgeToolbar className="dialog-header tyl-typography">
            <h1
              slot="start"
              className="forge-typography--heading4"
              id="schedule-send-modal-title"
              data-testid="schedule-send-modal-title"
            >
              {I18n.t('title', { scope })}
            </h1>
            <ForgeIconButton densityLevel={3} slot="end">
              <ForgeIcon name="close" onClick={onDismiss} />
            </ForgeIconButton>
          </ForgeToolbar>
        </div>

        <div
          slot="body"
          className={`schedule-send-modal-body ${isOnReadOnlyMode ? 'preview-mode' : ''}`}
          data-testid="schedule-send-modal-body"
        >
          <ForgeTextField {...textFieldAttrs}>
            <label htmlFor="schedule-name-field">{I18n.t('schedule_name_label', { scope })}</label>
            <input
              id="schedule-name-field"
              data-testid="schedule-name-field"
              type="text"
              value={scheduleSendValues.name ?? ''}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                updateScheduleSendValue('name', event.target.value);
              }}
              readOnly={isOnReadOnlyMode}
            />
          </ForgeTextField>
          <ForgeTextField {...textFieldAttrs}>
            <label htmlFor="email-subject-field">{I18n.t('email_subject_label', { scope })}</label>
            <input
              id="email-subject-field"
              data-testid="email-subject-field"
              type="text"
              value={scheduleSendValues.subject ?? ''}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                updateScheduleSendValue('subject', event.target.value);
              }}
              readOnly={isOnReadOnlyMode}
            />
          </ForgeTextField>
          <RecipientsField
            key={`recipients-${view.id}`}
            view={view}
            scheduledSendModalVisible={scheduledSendModalVisible}
            recipients={scheduleSendValues.recipients}
            updateScheduleSendValue={updateScheduleSendValue}
            readOnly={isOnReadOnlyMode}
          />

          {isOnReadOnlyMode ? (
            <List attachments={scheduleSendValues.attachmentConfigs} />
          ) : (
            <>
              <AttachmentSection
                tableList={tablesFromStoryData(view)}
                attachmentConfigs={scheduleSendValues.attachmentConfigs}
                updateScheduleSendValue={updateScheduleSendValue}
              />
              <FrequencySelector
                value={frequencyConfig}
                setValue={setFrequencyConfig}
                className="container-border"
              />
            </>
          )}
          <SummarySection scheduleSendValues={scheduleSendValues} />
        </div>

        <div slot="footer" className="schedule-send-footer">
          {isOnReadOnlyMode ? (
            <PreviewFooter onDelete={onDelete} onDismiss={onDismiss} onEdit={onEdit} />
          ) : (
            <Footer
              onDismiss={onDismiss}
              onPreview={onPreview}
              onSaveConfiguration={onSaveConfiguration}
              onUpdate={onUpdate}
              prevAction={prevAction}
              scheduleSendValues={scheduleSendValues}
            />
          )}
        </div>
      </ForgeScaffold>
    </ForgeDialog>
  );
};

export default ScheduleSendReportModal;
